import Header from '@di-shared/features/common/Header';
import React, {Suspense, useEffect} from 'react';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import VehicleSestKr from '@di-shared/features/home/kr/vehicle/VehicleSest.kr';
import DiagOdometer from '@di-shared/features/diag/DiagOdometer';
import DiagOdometerSkeleton from '@di-shared/features/diag/DiagOdometerSkeleton';
import HeaderCloseButton from '@di-shared/features/common/HeaderCloseButton';
import {ErrorBoundary} from 'react-error-boundary';
import {useSuspenseQuery} from '@tanstack/react-query';
import {getQueryKey} from '@shared/queryKeyMap';
import {diApi} from '@di-shared/api/di';
import environmentStore from '@di-shared/features/environment/environmentStore';
import useErrorCounter from '@di-shared/features/common/useErrorCounter';

export interface DiagContentsProps {
  vin: string;
  ccid: string;
}

const DIAG_ODOMETER_INDEX = 0;
const VEHICLE_SEST_INDEX = 1;
const ERROR_COUNT = 2;

function DiagContents({vin, ccid}: DiagContentsProps) {
  const {t} = useInferableTranslation();
  const puid = environmentStore.getState().puid;

  const {data: isCcsFullEnroll} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.vehicle.getVehicle), vin, puid],
    queryFn: () => diApi.vehicle.getVehicle({vin, puid}),
    select: (data) => data.ccsFullEnrollYn,
  });

  const {isError, emitError} = useErrorCounter(ERROR_COUNT);

  function onDiagOdometerNotRendered() {
    emitError(DIAG_ODOMETER_INDEX);
  }

  function onVehicleSestNotRendered() {
    emitError(VEHICLE_SEST_INDEX);
  }

  useEffect(() => {
    if (isCcsFullEnroll) {
      onDiagOdometerNotRendered();
    }
  }, [isCcsFullEnroll]);

  if (isError) {
    throw new Error();
  }

  return (
    <div className="wrap">
      <Header right={<HeaderCloseButton />}>
        <h1 className="headline_xxsmall nav_center_title">{t('T10149')}</h1>
      </Header>

      <main className="content page_home">
        {!isCcsFullEnroll && (
          <ErrorBoundary
            fallback={<></>}
            onError={onDiagOdometerNotRendered}
          >
            <Suspense fallback={<DiagOdometerSkeleton />}>
              <DiagOdometer vin={vin} />
            </Suspense>
          </ErrorBoundary>
        )}

        <VehicleSestKr
          vin={vin}
          ccid={ccid}
          onError={onVehicleSestNotRendered}
        />
      </main>
    </div>
  );
}

export default DiagContents;
